// Free-tier watermark, BURNED INTO the WebGL2 framebuffer.
//
// A DOM overlay (the old `.wm` div) is invisible to `gl.readPixels` — so the
// direct-canvas IPC stream path (frameStreamer, used whenever the fullscreen
// output window is closed) would ship a CLEAN feed for free users. The only
// capture-proof watermark is one drawn into the pixels themselves: call
// drawWatermark() right after host.render() and before the frame is captured,
// on the same context, for any non-entitled show. It then survives every path
// identically — canvas readback, SCK/WGC window capture, and the on-screen
// stage — because it IS the rendered image.
//
// Look matches the old `.wm` CSS: display font, centered, ~85% of frame width,
// 42% white with a soft shadow — deliberately gig-unusable.

interface Res {
  prog: WebGLProgram;
  vao: WebGLVertexArrayObject;
  buf: WebGLBuffer;
  tex: WebGLTexture;
  uTex: WebGLUniformLocation | null;
  texAspect: number; // width / height of the rasterized mark
}

const cache = new WeakMap<WebGL2RenderingContext, Res | null>();

// The brand display face (CSS --disp: Changa, a variable font at weight 700).
// One source of truth so the rasterizer and the font-ready check agree.
const WM_FONT_PX = 256;
const WM_FONT = `700 ${WM_FONT_PX}px Changa`; // Changa Bold

const VERT = `#version 300 es
in vec2 aPos; in vec2 aUv; out vec2 vUv;
void main() { vUv = aUv; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG = `#version 300 es
precision mediump float;
in vec2 vUv; out vec4 o; uniform sampler2D uTex;
void main() { o = texture(uTex, vUv); }`; // alpha (incl. the 0.42) is baked into the texture

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader | null {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

/** Rasterize "SYQEL" to a 2D canvas → an RGBA texture with premultiplied look. */
function rasterize(gl: WebGL2RenderingContext): { tex: WebGLTexture; aspect: number } | null {
  const text = 'SYQEL';
  const fontPx = WM_FONT_PX;
  const spacing = 0.14 * fontPx; // matches letter-spacing: 0.14em
  const font = `${WM_FONT}, system-ui, sans-serif`; // Changa Bold, system fallback
  const measure = document.createElement('canvas').getContext('2d');
  if (!measure) return null;
  measure.font = font;
  let textW = 0;
  for (const ch of text) textW += measure.measureText(ch).width + spacing;
  textW -= spacing;

  const padX = fontPx * 0.5;
  const padY = fontPx * 0.45;
  const c = document.createElement('canvas');
  c.width = Math.ceil(textW + padX * 2);
  c.height = Math.ceil(fontPx * 1.3 + padY * 2);
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.42)';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 2;
  let x = padX;
  const y = c.height / 2;
  for (const ch of text) {
    ctx.fillText(ch, x, y);
    x += ctx.measureText(ch).width + spacing;
  }

  const tex = gl.createTexture();
  if (!tex) return null;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return { tex, aspect: c.width / c.height };
}

function build(gl: WebGL2RenderingContext): Res | null {
  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    gl.deleteProgram(prog);
    return null;
  }
  const raster = rasterize(gl);
  if (!raster) {
    gl.deleteProgram(prog);
    return null;
  }
  const vao = gl.createVertexArray();
  const buf = gl.createBuffer();
  if (!vao || !buf) return null;
  const aPos = gl.getAttribLocation(prog, 'aPos');
  const aUv = gl.getAttribLocation(prog, 'aUv');
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, 24 * 4, gl.DYNAMIC_DRAW); // 6 verts × (2 pos + 2 uv) floats
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(aUv);
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8);
  gl.bindVertexArray(null);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return { prog, vao, buf, tex: raster.tex, uTex: gl.getUniformLocation(prog, 'uTex'), texAspect: raster.aspect };
}

function disposeRes(gl: WebGL2RenderingContext, res: Res): void {
  gl.deleteTexture(res.tex);
  gl.deleteBuffer(res.buf);
  gl.deleteVertexArray(res.vao);
  gl.deleteProgram(res.prog);
}

/** The texture rasterizes "SYQEL" in whatever "Changa" resolves to AT BUILD
 *  TIME. On the first frame the variable font is usually still loading, so
 *  canvas 2D silently falls back to a system face. Load Changa Bold, then drop
 *  the cache so the next draw rebuilds the mark in the real brand face. Runs at
 *  most once (after the rebuild the check passes). */
function ensureChangaLoaded(gl: WebGL2RenderingContext, built: Res): void {
  const fonts = typeof document !== 'undefined' ? document.fonts : null;
  if (!fonts) return;
  // Do NOT trust fonts.check() — in WKWebView it reports the variable font
  // "available" while canvas 2D still rasterizes a system fallback. Force the
  // load, wait for the whole set to settle, THEN rebuild the texture once so it
  // rasterizes in real Changa Bold. Idempotent no-op if the first build already
  // used Changa.
  Promise.all([fonts.load(WM_FONT), fonts.ready])
    .then(() => {
      if (cache.get(gl) === built) {
        disposeRes(gl, built);
        cache.delete(gl); // next drawWatermark() rebuilds with Changa loaded
      }
    })
    .catch(() => undefined);
}

/**
 * Draw the watermark into the currently-bound drawing buffer (w×h pixels).
 * Isolated in its own VAO + default framebuffer; restores blend state so the
 * engine's next frame is untouched. No-op if the GL resources can't build.
 */
export function drawWatermark(gl: WebGL2RenderingContext, w: number, h: number): void {
  let res = cache.get(gl);
  if (res === undefined) {
    res = build(gl);
    cache.set(gl, res); // cache null too — don't retry a broken build every frame
    if (res) ensureChangaLoaded(gl, res);
  }
  if (!res || w <= 0 || h <= 0) return;

  // Span ~85% of frame width, centered, preserving the mark's aspect.
  const halfW = 0.85;
  const halfH = (halfW * (w / h)) / res.texAspect;
  // two triangles: (x, y, u, v)
  const verts = new Float32Array([
    -halfW, -halfH, 0, 1,
    halfW, -halfH, 1, 1,
    -halfW, halfH, 0, 0,
    -halfW, halfH, 0, 0,
    halfW, -halfH, 1, 1,
    halfW, halfH, 1, 0,
  ]);

  gl.bindFramebuffer(gl.FRAMEBUFFER, null); // draw onto the canvas the world was rendered to
  gl.viewport(0, 0, w, h);
  gl.useProgram(res.prog);
  gl.bindVertexArray(res.vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, res.buf);
  gl.bufferSubData(gl.ARRAY_BUFFER, 0, verts);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, res.tex);
  gl.uniform1i(res.uTex, 0);
  const blendWas = gl.isEnabled(gl.BLEND);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // texture is premultiplied
  gl.disable(gl.DEPTH_TEST);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  if (!blendWas) gl.disable(gl.BLEND);
  gl.bindVertexArray(null);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.useProgram(null);
}
